import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/ui/data-table";
import type { DataTableColumn } from "@/components/ui/data-table";
import KlausurListCard from "@/features/klausur/components/klausurListCard.component";
import { useAuth } from "@/lib/contexts/auth.context";
import { KlausurService } from "@/lib/klausur/klausur.service";
import {
	OrgaAdminKlausurService,
	type Klausur,
} from "@/lib/orgaAdmin/orgaAdminKlausur.service";
import { OrgaAdminGruppeService } from "@/lib/orgaAdmin/orgaAdminGruppe.service";
import type { OrgaAdminGruppeDTO } from "@/lib/orgaAdmin/orgaAdmin.types";
import { Upload, Plus, Trash2, AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";
import MassUploadStudentKlausuren from "@/features/klausur/components/massUploadStudentKlausuren.component";
import SingleKlausurUpload from "@/features/klausur/components/singleKlausurUpload.component";
import StudentKlausurDialogs, {
	type StudentKlausurDialogsRef,
	type StudentKlausurWithKlausurInfo,
} from "@/features/klausur/components/studentKlausurDialogs.component";

/**
 * Page for all klausuren of an organization
 * - Manage and create klausuren
 * - Upload student klausuren (single and mass upload)
 */
export default function KlausurenPage() {
	const { currentOrganization } = useAuth();
	const navigate = useNavigate();
	const selectedOrganizationId = currentOrganization?.organizationId ?? "";
	const studentKlausurDialogsRef = useRef<StudentKlausurDialogsRef>(null);

	const [klausurs, setKlausurs] = useState<Klausur[]>([]);
	const [groups, setGroups] = useState<OrgaAdminGruppeDTO[]>([]);
	const [selectedGruppeId, setSelectedGruppeId] = useState<string>("");
	const [isLoadingKlausurs, setIsLoadingKlausurs] = useState(false);
	const [isLoadingGroups, setIsLoadingGroups] = useState(false);
	const [isCreateKlausurOpen, setIsCreateKlausurOpen] = useState(false);
	const [newKlausurTitle, setNewKlausurTitle] = useState("");
	const [isCreatingKlausur, setIsCreatingKlausur] = useState(false);
	const [isMassUploadOpen, setIsMassUploadOpen] = useState(false);
	const [selectedKlausurForUpload, setSelectedKlausurForUpload] =
		useState<Klausur | null>(null);
	const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
	const [showMassUploadCloseConfirmation, setShowMassUploadCloseConfirmation] =
		useState(false);
	const [hasUnsavedWork, setHasUnsavedWork] = useState(false);
	const [hasMassUploadUnsavedWork, setHasMassUploadUnsavedWork] =
		useState(false);
	const [
		isViewStudentKlausurenDialogOpen,
		setIsViewStudentKlausurenDialogOpen,
	] = useState(false);
	const [selectedKlausurForViewing, setSelectedKlausurForViewing] =
		useState<Klausur | null>(null);
	const [dialogSearchTerm, setDialogSearchTerm] = useState("");

	/**
	 * Load the klausuren for the current organization
	 */
	const loadKlausurs = useCallback(async () => {
		if (!selectedOrganizationId) return;
		setIsLoadingKlausurs(true);
		try {
			const service = OrgaAdminKlausurService.getInstance();
			const data = await service.getKlausurs(selectedOrganizationId);
			setKlausurs(data);
		} catch (error) {
			console.error("Error loading klausurs:", error);
			toast.error("Fehler beim Laden der Klausuren");
		} finally {
			setIsLoadingKlausurs(false);
		}
	}, [selectedOrganizationId]);

	/**
	 * Load the groups for the current organization
	 */
	const loadGroups = useCallback(async () => {
		if (!selectedOrganizationId) return;
		setIsLoadingGroups(true);
		try {
			const service = OrgaAdminGruppeService.getInstance();
			const data = await service.getGruppenInOrganization(
				selectedOrganizationId,
			);
			setGroups(data);
		} catch (error) {
			console.error("Error loading groups:", error);
			toast.error("Fehler beim Laden der Gruppen");
		} finally {
			setIsLoadingGroups(false);
		}
	}, [selectedOrganizationId]);

	/**
	 * Create a new klausur for the current organization
	 */
	const handleCreateKlausur = useCallback(async () => {
		const trimmedTitle = newKlausurTitle.trim();
		if (!selectedOrganizationId) {
			toast.error("Keine Organisation ausgewählt");
			return;
		}
		if (!trimmedTitle || isCreatingKlausur) return;

		setIsCreatingKlausur(true);
		try {
			const service = KlausurService.getInstance();
			await service.createKlausur(selectedOrganizationId, {
				title: trimmedTitle,
			});
			toast.success("Klausur erstellt");
			await loadKlausurs();
			setIsCreateKlausurOpen(false);
			setNewKlausurTitle("");
		} catch (error) {
			console.error("Error creating klausur:", error);
			toast.error("Fehler beim Erstellen der Klausur");
		} finally {
			setIsCreatingKlausur(false);
		}
	}, [
		isCreatingKlausur,
		loadKlausurs,
		newKlausurTitle,
		selectedOrganizationId,
	]);

	/**
	 * Handle closing the upload sheet with confirmation if there's unsaved work
	 */
	const handleCloseUploadSheet = useCallback(() => {
		if (hasUnsavedWork) {
			setShowCloseConfirmation(true);
		} else {
			setSelectedKlausurForUpload(null);
			setHasUnsavedWork(false);
		}
	}, [hasUnsavedWork]);

	const handleCloseMassUploadSheet = useCallback(() => {
		if (hasMassUploadUnsavedWork) {
			setShowMassUploadCloseConfirmation(true);
		} else {
			setIsMassUploadOpen(false);
			setHasMassUploadUnsavedWork(false);
		}
	}, [hasMassUploadUnsavedWork]);

	const handleConfirmClose = useCallback(() => {
		setSelectedKlausurForUpload(null);
		setHasUnsavedWork(false);
		setShowCloseConfirmation(false);
	}, []);

	const handleConfirmMassUploadClose = useCallback(() => {
		setIsMassUploadOpen(false);
		setHasMassUploadUnsavedWork(false);
		setShowMassUploadCloseConfirmation(false);
	}, []);

	/**
	 * Get all studentklausuren with their klausur info for the data table
	 */
	const allStudentKlausuren = useMemo<StudentKlausurWithKlausurInfo[]>(() => {
		const result: StudentKlausurWithKlausurInfo[] = [];

		for (const klausur of klausurs) {
			if (klausur.studentKlausuren && klausur.studentKlausuren.length > 0) {
				for (const studentKlausur of klausur.studentKlausuren) {
					const gruppe = groups.find((g) => g.id === studentKlausur.gruppeId);
					result.push({
						...studentKlausur,
						klausurTitle: klausur.title,
						klausurIndex: klausur.index,
						gruppeTitle: gruppe?.title || "Unbekannte Gruppe",
					});
				}
			}
		}

		return result;
	}, [klausurs, groups]);

	/**
	 * Handle delete student klausur
	 */
	const handleDeleteStudentKlausur = useCallback(
		async (studentKlausurId: string) => {
			if (!selectedOrganizationId) return;

			try {
				const service = OrgaAdminKlausurService.getInstance();
				await service.deleteStudentKlausur(
					selectedOrganizationId,
					studentKlausurId,
				);
				toast.success("Studentenklausur erfolgreich gelöscht");
				await loadKlausurs();
			} catch (error) {
				// Error will be handled by the dialog component
				throw error;
			}
		},
		[selectedOrganizationId, loadKlausurs],
	);

	/**
	 * Handle change ausschuss of student klausur
	 */
	const handleChangeAusschuss = useCallback(
		async (studentKlausurId: string, newGruppeId: string) => {
			if (!selectedOrganizationId) return;

			try {
				const service = OrgaAdminKlausurService.getInstance();
				await service.updateStudentKlausurGruppe(
					selectedOrganizationId,
					studentKlausurId,
					newGruppeId,
				);
				toast.success("Ausschuss erfolgreich geändert");
				await Promise.all([loadKlausurs(), loadGroups()]);
			} catch (error) {
				// Error will be handled by the dialog component
				throw error;
			}
		},
		[selectedOrganizationId, loadKlausurs, loadGroups],
	);

	/**
	 * Check if a student klausur has assigned korrektors
	 */
	const hasAssignedKorrektors = useCallback(
		(studentKlausur: StudentKlausurWithKlausurInfo): boolean => {
			if (!studentKlausur.korrekturSteps) return false;
			return studentKlausur.korrekturSteps.some(
				(step) => step.korrektorId !== null && step.korrektorId !== undefined,
			);
		},
		[],
	);

	/**
	 * Check if a student klausur has existing corrections
	 */
	const hasExistingKorrekturen = useCallback(
		(studentKlausur: StudentKlausurWithKlausurInfo): boolean => {
			if (!studentKlausur.korrekturSteps) return false;
			return studentKlausur.korrekturSteps.some(
				(step) =>
					step.korrekturStudentKlausuren &&
					step.korrekturStudentKlausuren.length > 0,
			);
		},
		[],
	);

	/**
	 * Filter student klausuren for the selected klausur (for the dialog view)
	 */
	const filteredStudentKlausuren = useMemo<
		StudentKlausurWithKlausurInfo[]
	>(() => {
		if (!selectedKlausurForViewing) return [];

		let filtered = allStudentKlausuren.filter(
			(sk) => sk.klausurId === selectedKlausurForViewing.id,
		);

		// Apply search filter
		if (dialogSearchTerm) {
			const lowerTerm = dialogSearchTerm.toLowerCase();
			filtered = filtered.filter((item) => {
				return (
					item.internalIdentifier.toLowerCase().includes(lowerTerm) ||
					item.gruppeTitle.toLowerCase().includes(lowerTerm)
				);
			});
		}

		return filtered;
	}, [allStudentKlausuren, selectedKlausurForViewing, dialogSearchTerm]);

	/**
	 * Define columns for the per-klausur student klausuren view (popup dialog)
	 */
	const perKlausurStudentKlausurColumns = useMemo<
		DataTableColumn<StudentKlausurWithKlausurInfo>[]
	>(
		() => [
			{
				key: "internalIdentifier",
				label: "Identifier",
				sortable: true,
				accessor: (item) => item.internalIdentifier,
			},
			{
				key: "gruppeTitle",
				label: "Ausschuss / Gruppe",
				sortable: true,
				accessor: (item) => item.gruppeTitle,
			},
			{
				key: "actions",
				label: "Aktionen",
				render: (item) => {
					const hasKorrektors = hasAssignedKorrektors(item);
					const hasKorrekturen = hasExistingKorrekturen(item);
					return (
						<div className="space-y-2">
							<div className="flex items-center gap-2">
								<Button
									variant="outline"
									size="sm"
									onClick={() => {
										studentKlausurDialogsRef.current?.openChangeAusschussDialog(
											item,
										);
									}}
									disabled={hasKorrektors}
									title={
										hasKorrektors
											? "Ausschuss kann nicht geändert werden - Korrektor*innen zugewiesen"
											: "Ausschuss ändern"
									}
								>
									<AlertTriangle className="h-4 w-4 mr-1" />
									Ausschuss ändern
								</Button>
								<Button
									variant="destructive"
									size="sm"
									onClick={() => {
										studentKlausurDialogsRef.current?.openDeleteDialog(item);
									}}
									disabled={hasKorrekturen}
									title={
										hasKorrekturen
											? "Löschen nicht möglich - Korrekturen vorhanden"
											: "Studentenklausur löschen"
									}
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							</div>
							{(hasKorrektors || hasKorrekturen) && (
								<div className="space-y-1">
									{hasKorrektors && (
										<div className="flex items-start gap-1 text-xs text-amber-600">
											<AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
											<span>Korrektor*innen zugewiesen</span>
										</div>
									)}
									{hasKorrekturen && (
										<div className="flex items-start gap-1 text-xs text-destructive">
											<AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
											<span>Korrekturen vorhanden</span>
										</div>
									)}
								</div>
							)}
						</div>
					);
				},
			},
		],
		[hasAssignedKorrektors],
	);

	/**
	 * Load the klausuren and groups for the current organization when the organization is selected
	 */
	useEffect(() => {
		if (selectedOrganizationId) {
			loadKlausurs();
			loadGroups();
		}
	}, [selectedOrganizationId, loadKlausurs, loadGroups]);

	const selectedOrganization = currentOrganization;

	return (
		<div className="w-full p-6 space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold">Klausuren</h1>
					<p className="text-muted-foreground">
						Klausuren verwalten, erstellen und Studentenklausuren hochladen
					</p>
				</div>
				<div className="flex items-center gap-2">
					<Button
						variant="outline"
						className="flex items-center gap-2"
						onClick={() => setIsCreateKlausurOpen(true)}
						disabled={!selectedOrganizationId}
					>
						<Plus className="h-4 w-4" />
						Klausur erstellen
					</Button>
					{/* <Button
						variant="outline"
						className="flex items-center gap-2"
						onClick={() => setIsMassUploadOpen(true)}
						disabled={!selectedOrganizationId}
					>
						<Upload className="h-4 w-4" />
						Mehrfach-Upload
					</Button> */}
				</div>
			</div>

			{/* Quick Stats */}
			{selectedOrganizationId && (
				<div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
					<div>
						Organisation:{" "}
						<span className="font-medium text-foreground">
							{selectedOrganization?.name || selectedOrganizationId}
						</span>
					</div>
					<div>
						Klausuren:{" "}
						<span className="font-medium text-foreground">
							{isLoadingKlausurs ? "..." : klausurs.length}
						</span>
					</div>
					<div>
						Studentenklausuren:{" "}
						<span className="font-medium text-foreground">
							{isLoadingKlausurs
								? "..."
								: klausurs.reduce(
										(sum, k) => sum + (k.studentKlausuren?.length || 0),
										0,
									)}
						</span>
					</div>
				</div>
			)}

			{/* Create Klausur Dialog */}
			<Dialog open={isCreateKlausurOpen} onOpenChange={setIsCreateKlausurOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Neue Klausur erstellen</DialogTitle>
						<DialogDescription>
							Geben Sie den Titel der Klausur ein. Nach dem Erstellen können Sie
							Studentenklausuren hochladen.
						</DialogDescription>
					</DialogHeader>
					<div className="py-2">
						<Input
							value={newKlausurTitle}
							onChange={(e) => setNewKlausurTitle(e.target.value)}
							placeholder="Klausurtitel"
							autoFocus
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleCreateKlausur();
								}
							}}
						/>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsCreateKlausurOpen(false)}
						>
							Abbrechen
						</Button>
						<Button
							onClick={handleCreateKlausur}
							disabled={
								isCreatingKlausur || newKlausurTitle.trim().length === 0
							}
						>
							Erstellen
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Mass Upload Dialog */}
			<Dialog
				open={isMassUploadOpen}
				onOpenChange={(open) => !open && handleCloseMassUploadSheet()}
			>
				<DialogContent
					className="max-w-3xl w-full"
					onInteractOutside={(e) => e.preventDefault()}
					onEscapeKeyDown={(e) => {
						e.preventDefault();
						handleCloseMassUploadSheet();
					}}
				>
					<DialogHeader className="border-b border-gray-200 pb-4">
						<div className="flex items-center gap-2">
							<Upload className="h-5 w-5 text-blue-600" />
							<DialogTitle className="text-xl">
								Mehrere Klausuren gleichzeitig hochladen
							</DialogTitle>
						</div>
						<DialogDescription className="text-gray-600">
							Laden Sie mehrere PDFs hoch und ordnen Sie diese den Klausuren zu.
							Für einzelne Klausuren nutzen Sie den Upload-Button direkt in der
							Klausurkarte.
						</DialogDescription>
					</DialogHeader>
					<div className="max-h-[70vh] overflow-y-auto pt-4">
						{selectedOrganizationId && (
							<MassUploadStudentKlausuren
								klausurs={klausurs}
								groups={groups}
								selectedOrganizationId={selectedOrganizationId}
								selectedGruppeId={selectedGruppeId}
								onGruppeChange={setSelectedGruppeId}
								isLoadingKlausurs={isLoadingKlausurs}
								isLoadingGroups={isLoadingGroups}
								onUploadComplete={() => {
									loadKlausurs();
									setHasMassUploadUnsavedWork(false);
								}}
								onFilesChanged={(hasFiles: boolean) =>
									setHasMassUploadUnsavedWork(hasFiles)
								}
							/>
						)}
					</div>
				</DialogContent>
			</Dialog>

			{/* Mass Upload Close Confirmation Dialog */}
			<Dialog
				open={showMassUploadCloseConfirmation}
				onOpenChange={setShowMassUploadCloseConfirmation}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Nicht gespeicherte Änderungen</DialogTitle>
						<DialogDescription>
							Sie haben Dateien hochgeladen, die noch nicht gespeichert wurden.
							Möchten Sie wirklich schließen? Alle nicht gespeicherten Dateien
							gehen verloren.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setShowMassUploadCloseConfirmation(false)}
						>
							Abbrechen
						</Button>
						<Button
							variant="destructive"
							onClick={handleConfirmMassUploadClose}
						>
							Schließen
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Single Klausur Upload Dialog */}
			<Dialog
				open={!!selectedKlausurForUpload}
				onOpenChange={(open) => !open && handleCloseUploadSheet()}
			>
				<DialogContent
					className="max-w-3xl w-full bg-card"
					onInteractOutside={(e) => e.preventDefault()}
					onEscapeKeyDown={(e) => {
						e.preventDefault();
						handleCloseUploadSheet();
					}}
				>
					<DialogHeader className="border-b border-gray-200 pb-4">
						<div className="flex items-center gap-2">
							<Upload className="h-5 w-5 text-primary" />
							<DialogTitle className="text-xl">
								Studentenklausuren hochladen
							</DialogTitle>
						</div>
						<DialogDescription className="text-gray-600">
							Wählen Sie die Gruppe und laden Sie die PDFs für diese Klausur
							hoch.
						</DialogDescription>
					</DialogHeader>
					<div className="max-h-[70vh] overflow-y-auto pt-4">
						{selectedKlausurForUpload && (
							<SingleKlausurUpload
								klausur={selectedKlausurForUpload}
								groups={groups}
								selectedOrganizationId={selectedOrganizationId}
								isLoadingGroups={isLoadingGroups}
								onUploadComplete={() => {
									loadKlausurs();
									setHasUnsavedWork(false);
									setSelectedKlausurForUpload(null);
								}}
								onFilesChanged={(hasFiles) => setHasUnsavedWork(hasFiles)}
							/>
						)}
					</div>
				</DialogContent>
			</Dialog>

			{/* Single Upload Close Confirmation Dialog */}
			<Dialog
				open={showCloseConfirmation}
				onOpenChange={setShowCloseConfirmation}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Nicht gespeicherte Änderungen</DialogTitle>
						<DialogDescription>
							Sie haben Dateien hochgeladen, die noch nicht gespeichert wurden.
							Möchten Sie wirklich schließen? Alle nicht gespeicherten Dateien
							gehen verloren.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setShowCloseConfirmation(false)}
						>
							Abbrechen
						</Button>
						<Button variant="destructive" onClick={handleConfirmClose}>
							Schließen
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Klausur List with Create and Upload Actions */}
			{selectedOrganizationId && (
				<KlausurListCard
					klausurs={klausurs}
					isLoading={isLoadingKlausurs}
					onKlausurClick={(k) => navigate(`/orga-admin/klausur/${k.id}`)}
					onUploadClick={(klausur) => setSelectedKlausurForUpload(klausur)}
					onViewStudentKlausurenClick={(klausur) => {
						setSelectedKlausurForViewing(klausur);
						setIsViewStudentKlausurenDialogOpen(true);
					}}
				/>
			)}

			{/* View Student Klausuren Dialog (per klausur) */}
			<Dialog
				open={isViewStudentKlausurenDialogOpen}
				onOpenChange={(open) => {
					setIsViewStudentKlausurenDialogOpen(open);
					if (!open) {
						setDialogSearchTerm("");
					}
				}}
			>
				<DialogContent className="max-w-6xl w-full h-[90vh] flex flex-col p-0 bg-card">
					<DialogHeader className="p-6 pb-4">
						<DialogTitle>
							Studentenklausuren - {selectedKlausurForViewing?.title}
						</DialogTitle>
						<DialogDescription>
							Übersicht aller hochgeladenen Studentenklausuren für diese Klausur
						</DialogDescription>
					</DialogHeader>

					{/* Sticky Search Bar */}
					<div className="px-6 pb-4">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
							<Input
								placeholder="Suche nach Identifier oder Ausschuss..."
								value={dialogSearchTerm}
								onChange={(e) => setDialogSearchTerm(e.target.value)}
								className="pl-10"
							/>
						</div>
					</div>

					{/* Scrollable Content */}
					<div className="flex-1 overflow-y-auto px-6 pb-6">
						{selectedKlausurForViewing && (
							<DataTable
								data={filteredStudentKlausuren}
								columns={perKlausurStudentKlausurColumns}
								defaultSortField="internalIdentifier"
								defaultSortOrder="asc"
								emptyMessage="Keine Studentenklausuren für diese Klausur gefunden"
								showPagination={false}
							/>
						)}
					</div>
				</DialogContent>
			</Dialog>

			{/* Student Klausur Management Dialogs */}
			<StudentKlausurDialogs
				ref={studentKlausurDialogsRef}
				onDeleteStudentKlausur={handleDeleteStudentKlausur}
				onChangeAusschuss={handleChangeAusschuss}
				groups={groups}
				isLoadingGroups={isLoadingGroups}
			/>
		</div>
	);
}
